import React, { createContext, useState, useEffect, useCallback, useRef, act } from "react";
import { getAPIURL, setCachedBusiness, getCachedBusiness } from "../lib/utils";
import { withActiveToken } from "../lib/auth";
import { useRouter } from "next/router";
import { useContext } from "react";
import UserContext from "./user-context";
import Loading from "../components/ui/loading";

const BusinessContext = createContext({
  businessList: null,
  activeBusiness: null,
  fetchBusinessList: () => { },
  setActiveBusiness: () => { },
  fetchStaffList: () => { },
  staffList: null,
  setStaffList: () => { },
  teammates: null,
  activeStaffs: null,
  pendingStaffs: null,
  setNotificationContent: () => { },
  notificationContent: {
    showNotification: false,
    header: '',
    message: '',
    icon: null,
  },
  isOwner: false,
  installedPegs: null,
  pegCheckReady: false,
});

export const BusinessProvider = (props) => {
  const [businessList, setBusinessList] = useState(null);
  const [activeBusiness, setBusiness] = useState(null);
  const [staffList, setStaffList] = useState(null);
  const { user } = useContext(UserContext);
  const router = useRouter();
  const activeStaffs = staffList ? staffList.filter(staff => staff.user.email !== '') : null;
  const pendingStaffs = staffList ? staffList.filter(staff => staff.user.email === '') : null;
  const [notificationContent, setNotificationContent] = useState({
    showNotification: false,
    header: '',
    message: '',
    icon: null,
  });
  const currentUser = staffList ? staffList.find(staff => staff.user.email === user?.email) : null;
  const owner = staffList?.find(staff => staff.role === 'owner')
  const isOwner = currentUser?.role === 'owner';
  const [pegIsInstalled, setPegIsInstalled] = useState(null);
  const [installedPegs, setInstalledPegs] = useState(null);
  const [pegCheckReady, setPegCheckReady] = useState(false);
  // The business whose pegs are on screen, so an answer that arrives after
  // the person has switched business is dropped rather than shown.
  const shownBusiness = useRef(null);
  shownBusiness.current = activeBusiness?.uuid ?? null;



  const fetchBusinessList = useCallback(() => {
    return withActiveToken(() => {
      fetch(getAPIURL("businesses/"), {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            if (json.length === 0) {
              router.push("/businesses/new");
              return;
            }

            setBusinessList(json);

            const businessUuid = router.query.uuid

            if (businessUuid) {
              const activeBusiness = json.find(
                (business) => business.uuid == businessUuid
              );

              if (activeBusiness) {
                setActiveBusiness(activeBusiness);
                return;
              }
            }

            const cachedBusiness = getCachedBusiness();

            const activeBusiness = cachedBusiness
              ? json.find((business) => business.uuid == cachedBusiness) ||
              json[0]
              : json[0];
            setActiveBusiness(activeBusiness);
          });
        }
        return res.json().then((json) => Promise.reject(json));
      });
    }, router);
  }, [router]);

  const fetchStaffList = useCallback(() => {
    return withActiveToken(() => {
      fetch(getAPIURL(`businesses/${activeBusiness?.uuid}/staff/`), {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            setStaffList(json);
          });
        }
        return res.json().then((json) => Promise.reject(json));
      });
    });
  }
  );

  // The installed pegs as the api lists them now; installs nothing. An
  // install can bring others with it (the api installs Newtmail along with
  // emails), so after one the list is read again, once, rather than taken
  // to be the old list plus the install's own answer.
  const rereadInstalledPegs = (business) => {
    return withActiveToken(() => {
      fetch(getAPIURL(`businesses/${business}/pegs/`), {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((json) => {
          if (!Array.isArray(json) || shownBusiness.current !== business) return;
          setInstalledPegs(json);
          const names = json.map((install) => install?.peg?.name).filter((name) => typeof name === 'string');
          localStorage.setItem(`${business}_installed_pegs`, JSON.stringify(names));
        })
        .catch(() => {});
    }, router);
  };

  const installPeg = (peg) => {
    const payload = {
      "urn": `${peg}:1.0`,
      "skip_setup": true,
    }

    return withActiveToken(() => {
      fetch(getAPIURL(`businesses/${activeBusiness?.uuid}/pegs/`), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify( payload ),
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            setInstalledPegs((prev) => [...prev, json]);
            const names = [...(installedPegs?.map(peg => peg.peg.name) ?? []), json.peg.name];
            localStorage.setItem(`${getCachedBusiness()}_installed_pegs`, JSON.stringify(names));
            rereadInstalledPegs(activeBusiness?.uuid);
          });
        }
        return res.json().then((json) => Promise.reject(json));
      });
    });
  }

  const fetchInstalledPegs = () => {

    return withActiveToken(() => {
      fetch(getAPIURL(`businesses/${activeBusiness?.uuid}/pegs/`), {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            const names = json.map(peg => peg.peg.name);
            const installed = json.some(peg => peg.peg.name === 'emails');

            setInstalledPegs(json);
            setPegIsInstalled(installed);
            localStorage.setItem(`${getCachedBusiness()}_installed_pegs`, JSON.stringify(names));
            setPegCheckReady(true);

            if (!installed) {
              installPeg('emails');
            }
          });
        }
        return res.json().then((json) => {
          setPegCheckReady(true);
          return Promise.reject(json);
        });
      });
    }, router);
  };

  useEffect(() => {
    if (businessList == null && router.isReady) {
      fetchBusinessList();
    }
  }, [businessList, fetchBusinessList, router.isReady, activeBusiness]);

  useEffect(() => {
    if (activeBusiness) {
      setPegCheckReady(false);
      setInstalledPegs(null);
      fetchStaffList();
      fetchInstalledPegs();
    }
  }, [activeBusiness]);

  function setActiveBusiness(business) {
    setBusiness(business);
    setCachedBusiness(business.uuid);
  }
  // Add the updateBusinessList function
  function updateBusinessList(newList) {
    setBusinessList(newList); // Update the business list state
  }
  const teammates = staffList ? staffList.filter(staff => staff.user.first_name) : null;

  const context = {
    businessList,
    activeBusiness,
    fetchBusinessList,
    setActiveBusiness,
    updateBusinessList,
    fetchStaffList,
    staffList,
    setStaffList,
    teammates,
    activeStaffs,
    pendingStaffs,
    setNotificationContent,
    notificationContent,
    isOwner,
    installedPegs,
    pegCheckReady,
  };

  return (
    <BusinessContext.Provider value={context}>
      {!activeBusiness || !pegCheckReady
      ? <Loading /> 
      : props.children
    }
    </BusinessContext.Provider>
  );
};

export default BusinessContext;
